import { useAuth } from '@clerk/react-router';
import { isNonEmptyString } from '@sniptt/guards';
import { APP_PATHS } from 'app-shared/constants';
import { Navigate, Outlet } from 'react-router-dom';

import { ClerkSignInRedirect } from '@/auth/clerk/components/ClerkSignInRedirect';
import { ClerkApolloCacheResetEffect } from '@/auth/clerk/effect-components/ClerkApolloCacheResetEffect';
import { ClerkSignOutEffect } from '@/auth/clerk/effect-components/ClerkSignOutEffect';
import { ClerkUserProfileSyncEffect } from '@/auth/clerk/effect-components/ClerkUserProfileSyncEffect';
import { apolloCacheSessionKeyState } from '@/auth/clerk/states/apolloCacheSessionKeyState';
import { isSignedOutState } from '@/session/states/isSignedOutState';
import { FullScreenLoadingState } from '@/ui/feedback/loading-state/components/FullScreenLoadingState';
import { useAtomStateValue } from '@/ui/utilities/state/jotai/hooks/useAtomStateValue';

// In front of every page with Clerk sign-in: waits for Clerk, then requires a
// signed-in person with an active workspace. This only decides what to show;
// the server checks the session token on every request.
export const ClerkAuthGate = () => {
  // A pending session (signed in, no workspace yet) counts as signed in here,
  // so the person is sent to create or pick a workspace, not to sign in.
  const { isLoaded, isSignedIn, userId, orgId } = useAuth({
    treatPendingAsSignedOut: false,
  });
  const isSignedOut = useAtomStateValue(isSignedOutState);
  const apolloCacheSessionKey = useAtomStateValue(apolloCacheSessionKeyState);

  if (!isLoaded) {
    return <FullScreenLoadingState />;
  }

  if (isSignedOut) {
    return (
      <>
        <ClerkSignOutEffect />
        <FullScreenLoadingState />
      </>
    );
  }

  if (!isSignedIn) {
    return <ClerkSignInRedirect />;
  }

  if (!isNonEmptyString(orgId)) {
    return <Navigate to={APP_PATHS.onboardingWorkspace} replace />;
  }

  // Another person or workspace than the one the cached data belongs to:
  // the cache is cleared before any page shows, so data from one workspace
  // never appears in another.
  const sessionKey = `${userId}:${orgId}`;

  if (apolloCacheSessionKey !== sessionKey) {
    return (
      <>
        <ClerkApolloCacheResetEffect sessionKey={sessionKey} />
        <FullScreenLoadingState />
      </>
    );
  }

  return (
    <>
      <ClerkUserProfileSyncEffect />
      <Outlet />
    </>
  );
};
